import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, Loader2, Info, RefreshCcw, ArrowUp, ArrowDown, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { fetchVendorSubLedgerReport, fetchVendors, currencyCodeToSymbolMap, fetchVendorSubledgerReportCSV, goodsReceiptDocument } from '../apiHelper';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import GoodsReceiptDocument from './GoodsreceiptDocument';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../components/ui/Modal';

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '$';
    const isNegative = amount < 0;
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Math.abs(amount));
    return `${isNegative ? '-' : ''}${symbol}${formatted}`;
};

const processAccountData = (rawLedger, selectedId, endDate) => {
    if (!rawLedger || !selectedId || !rawLedger[selectedId]) return [];

    const vendorData = rawLedger[selectedId];
    const transactions = vendorData.transactions;

    // SYNC POINT: Initialize runningBalance with the backend's opening_balance
    // This ensures continuity from previous periods
    let runningBalance = parseFloat(vendorData.opening_balance || 0);

    const grouped = {};
    transactions.forEach(p => {
        if (!grouped[p.transaction_id]) grouped[p.transaction_id] = [];
        grouped[p.transaction_id].push(p);
    });

    const sortedTxIds = Object.keys(grouped).sort((a, b) => {
        const dateA = new Date(grouped[a][0].transaction_date);
        const dateB = new Date(grouped[b][0].transaction_date);
        return dateA - dateB || a - b;
    });

    const flattenedRows = [];

    // OPTIONAL: Add an explicit Opening Balance row for the UI
    if (flattenedRows.length === 0) {
        flattenedRows.push({
            is_opening_summary: true,
            transaction_date: null,
            description: "BALANCE BROUGHT FORWARD",
            cumulative_balance: runningBalance,
            currency_code: transactions[0]?.currency_code || 'NGN'
        });
    }

    sortedTxIds.forEach(txId => {
        const postings = grouped[txId];
        const sortedPostings = [...postings].sort((a, b) => {
            if (a.posting_type === 'debit' && b.posting_type === 'credit') return -1;
            return a.posting_type === 'credit' && b.posting_type === 'debit' ? 1 : 0;
        });

        sortedPostings.forEach((p, index) => {
            const isLiabilityAccount = p.account_type === 'liability' || p.account_code === '2001';

            if (isLiabilityAccount) {
                if (p.posting_type === 'credit') {
                    runningBalance += parseFloat(p.amount);
                } else {
                    runningBalance -= parseFloat(p.amount);
                }
            }

            flattenedRows.push({
                ...p,
                cumulative_balance: runningBalance,
                is_first_in_tx: index === 0,
                is_liability_side: isLiabilityAccount
            });
        });
    });

    return flattenedRows;
};

// const processAccountData = (rawLedger, selectedId, endDate) => {
//     if (!rawLedger || !selectedId || !rawLedger[selectedId]) return [];

//     const transactions = rawLedger[selectedId].transactions;
//     const grouped = {};

//     // Group postings by transaction_id
//     transactions.forEach(p => {
//         if (!grouped[p.transaction_id]) grouped[p.transaction_id] = [];
//         grouped[p.transaction_id].push(p);
//     });

//     const sortedTxIds = Object.keys(grouped).sort((a, b) => {
//         const dateA = new Date(grouped[a][0].transaction_date);
//         const dateB = new Date(grouped[b][0].transaction_date);
//         return dateA - dateB || a - b;
//     });

//     let runningBalance = 0;
//     const flattenedRows = [];

//     sortedTxIds.forEach(txId => {
//         const postings = grouped[txId];

//         // Sort: Debit Line ABOVE Credit Line
//         const sortedPostings = [...postings].sort((a, b) => {
//             if (a.posting_type === 'debit' && b.posting_type === 'credit') return -1;
//             if (a.posting_type === 'credit' && b.posting_type === 'debit') return 1;
//             return 0;
//         });

//         sortedPostings.forEach((p, index) => {
//             // MATH: Running balance only changes on the Liability (AP) side of the entry
//             const isLiabilityAccount = p.account_type === 'liability' || p.account_code === '2001';

//             if (isLiabilityAccount) {
//                 if (p.posting_type === 'credit') {
//                     runningBalance += parseFloat(p.amount); // Increase debt
//                 } else {
//                     runningBalance -= parseFloat(p.amount); // Decrease debt
//                 }
//             }

//             flattenedRows.push({
//                 ...p,
//                 cumulative_balance: runningBalance,
//                 is_first_in_tx: index === 0,
//                 is_liability_side: isLiabilityAccount
//             });
//         });
//     });

//     return flattenedRows;
// };


const VendorSubledgerReport = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);

    // 1. & 5. Replaced Account states with Vendor states
    const [vendors, setVendors] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState('');
    const [orgName, setOrgName] = useState("");

    const [selectedCreatorId, setSelectedCreatorId] = useState('');
    const [creators, setCreators] = useState([]);

    const [loading, setLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [vendorsLoading, setVendorsLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [currentGoodsReceipt, setCurrentGoodsReceipt] = useState(null);
    const [isFetchingGoodsReceipt, setIsFetchingGoodsReceipt] = useState(false);
    const [isGoodsReceiptModalOpen, setIsGoodsReceiptModalOpen] = useState(false);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            loadVendors(parsedUser); // 4. Calling fetchVendors with user
        }
    }, []);

    const loadVendors = async (currentUser) => {
        setVendorsLoading(true);
        try {
            const fetchedVendors = await fetchVendors(currentUser);
            // 5. Mapping vendorname and vendor_id
            const mappedVendors = fetchedVendors.map(v => ({
                value: v.vendor_id,
                label: v.vendorname,
            }));

            setVendors(mappedVendors);

            const passedVendorId = location.state?.vendor_id;
            if (passedVendorId && mappedVendors.some(v => v.value === passedVendorId)) {
                const today = new Date();
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(today.getDate() - 7);
                setEndDate(today.toISOString().split('T')[0]);
                setStartDate(oneWeekAgo.toISOString().split('T')[0]);
                setSelectedVendorId(passedVendorId);
            }
        } catch (err) {
            showToast("Failed to load vendors.", 'error');
        } finally {
            setVendorsLoading(false);
        }
    };

    const fetchReport = useCallback(async () => {
        if (!user || !selectedVendorId) return;
        setLoading(true);
        try {
            // 6. Formulated new params with vendor_id
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                vendor_id: selectedVendorId,
                creator_id: selectedCreatorId || null
            };
            const response = await fetchVendorSubLedgerReport(params);
            // console.log(response) // DO NOT REMOVE THIS LINE
            setOrgName(response.organization_name || "Vendor Ledger");

            const processed = processAccountData(response.general_ledger, selectedVendorId, endDate);
            setReportData(processed);

            // --- AGGREGATE CREATORS ---
            const accountData = response.general_ledger[selectedVendorId];
            if (accountData?.transactions) {
                const creatorMap = new Map();
                accountData.transactions.forEach(t => {
                    if (t.creator_id && t.creator_name) {
                        creatorMap.set(t.creator_id, t.creator_name);
                    }
                });

                const creatorOptions = Array.from(creatorMap.entries()).map(([id, name]) => ({
                    value: id,
                    label: name
                }));
                setCreators(creatorOptions);
            }
        } catch (err) {
            showToast("Failed to load vendor ledger.", "error");
        } finally {
            setLoading(false);
        }
    }, [user, startDate, endDate, selectedVendorId, endDate, selectedCreatorId, showToast]);

    const handleDownloadCSV = async () => {
        if (!user || !selectedVendorId) return;
        setCsvLoading(true);
        try {
            // 6. Formulated CSV params with vendor_id
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                vendor_id: selectedVendorId,
                creator_id: selectedCreatorId || null
            };
            const csvContent = await fetchVendorSubledgerReportCSV(params);

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Vendor_Ledger_${startDate}_to_${endDate}.csv`);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast("CSV Downloaded successfully", "success");
        } catch (err) {
            showToast("Failed to download CSV.", "error");
        } finally {
            setCsvLoading(false);
        }
    };

    const handleDownloadDisplayedCSV = () => {
        if (reportData.length === 0) {
            showToast("No data to export", "error");
            return;
        }

        let csvContent = "Date,Description,Reference,Invoiced/Purchase,Payment,Balance Owed\n";

        reportData.forEach(t => {
            const date = t.is_opening
                ? "OPENING BALANCE"
                : new Date(t.transaction_date).toLocaleDateString('en-GB');

            const description = t.is_opening
                ? "Opening Balance (carried forward)"
                : t.is_closing
                    ? "Closing Balance"
                    : (t.description || "").replace(/,/g, " "); // escape commas

            const ref = t.ref || "";

            const invoiced = (t.display_type === 'credit' || t.display_type === 'cash_purchase' || t.is_opening || t.is_closing)
                ? t.display_amount.toFixed(2)
                : "";

            const payment = (t.display_type === 'debit' ||
                (t.display_type === 'cash_purchase' && t.liability_total > 0))
                ? (t.inventory_total - (t.liability_total || 0)).toFixed(2)
                : "";

            const balance = t.cumulative_balance.toFixed(2);

            csvContent += `"${date}","${description}","${ref}",${invoiced},${payment},${balance}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Vendor_Ledger_Displayed_${startDate}_to_${endDate}.csv`);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast("Displayed Ledger CSV downloaded successfully", "success");
    };

    useEffect(() => {
        if (selectedVendorId) fetchReport();
    }, [selectedVendorId, selectedCreatorId, fetchReport]);

    const finalBalance = reportData.length > 0
        ? reportData[reportData.length - 1].cumulative_balance
        : 0;

    const totals = useMemo(() => {
        let debits = 0, credits = 0, cc = 'USD';
        if (reportData.length > 0) {
            cc = reportData[0].currency_code;
            reportData.forEach(t => {
                if (t.posting_type === 'debit') debits += t.amount;
                else credits += t.amount;
            });
        }
        return { debit: debits, credit: credits, currency: cc };
    }, [reportData]);

    const handlePurchaseOrder = async (po_number) => {
        if (!po_number) return;

        setIsFetchingGoodsReceipt(true);
        setIsGoodsReceiptModalOpen(true);

        try {
            const response = await goodsReceiptDocument({ po_number })
            setCurrentGoodsReceipt(response);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Error fetching goods receipt document";
            showToast(errorMessage, "error");
            setIsGoodsReceiptModalOpen(false);
        } finally {
            setIsFetchingGoodsReceipt(false);
        }
    }


    return (
        <div className="p-4 md:p-8 bg-[#f8f9fa] min-h-screen relative text-slate-900">
            <div className="max-w-6xl mx-auto">
                <header className="mb-6">
                    <Button onClick={() => navigate(-1)} variant="ghost" className="p-0 h-auto mb-4 text-slate-500 hover:text-indigo-600 flex items-center">
                        <ChevronLeft size={16} /> <span className="text-xs uppercase tracking-wider font-bold">Back to Reports</span>
                    </Button>

                    <div className="mb-4">
                        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{orgName || "Vendor Ledger"}</h1>
                        <p className="text-sm text-slate-500 font-medium font-mono">Ledger Extract</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                        {/* 5. Vendor Select replacement */}
                        <div className="md:col-span-1">
                            <SearchableSelectBox
                                label="Select Vendor"
                                options={vendors}
                                value={selectedVendorId}
                                onChange={(e) => setSelectedVendorId(e.target.value)}
                                loading={vendorsLoading}
                                placeholder="Choose vendor..."
                                containerClassName="mb-0"
                            />
                        </div>

                        {/* Creator Select */}
                        <div className="md:col-span-1">
                            <SearchableSelectBox
                                label="Filter by Creator"
                                options={creators}
                                value={selectedCreatorId}
                                onChange={(e) => setSelectedCreatorId(e.target.value)}
                                loading={loading}
                                placeholder="All Creators"
                                containerClassName="mb-0"
                                isClearable={true}
                            />
                        </div>

                        {/* Date Range */}
                        <div className="md:col-span-2 flex gap-2 mb-4">
                            <div className="flex-1">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">From</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">To</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full text-xs border rounded-lg p-[9px] outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 md:col-span-1">
                            {/* Existing Raw CSV Download */}
                            <Button
                                onClick={handleDownloadCSV}
                                disabled={csvLoading || !selectedVendorId}
                                className="bg-emerald-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-emerald-700 disabled:bg-slate-200"
                                title="Download Raw CSV from Server"
                            >
                                {csvLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                            </Button>

                            {/* NEW: Displayed Ledger CSV */}
                            <Button
                                onClick={handleDownloadDisplayedCSV}
                                disabled={!selectedVendorId || reportData.length === 0}
                                className="bg-indigo-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-indigo-700 disabled:bg-slate-200"
                                title="Download Displayed Ledger as CSV"
                            >
                                <FileSpreadsheet size={18} />
                            </Button>

                            {/* Refresh Button */}
                            <Button
                                onClick={fetchReport}
                                disabled={loading || !selectedVendorId}
                                className="bg-indigo-600 text-white w-[42px] h-[42px] p-0 flex items-center justify-center rounded-lg shadow-md hover:bg-indigo-700"
                                title="Refresh Report"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <RefreshCcw size={18} />}
                            </Button>
                        </div>

                    </div>
                </header>

                <Card className="overflow-hidden border-none shadow-xl rounded-2xl">
                    {loading ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Loader2 className="animate-spin mb-2" size={32} />
                            <p>Fetching Ledger...</p>
                        </div>
                    ) : reportData.length === 0 ? (
                        <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                            <Info size={32} className="mb-2 opacity-20" />
                            <p>No transactions found for this vendor in the selected period.</p>
                        </div>
                    ) : (
                        <div className="bg-white">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-28">Date</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Transaction Details (Double Entry)</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Ref</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Creator</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-rose-600 uppercase tracking-widest text-right">Debit (Payment)</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-emerald-600 uppercase tracking-widest text-right">Credit (Invoiced)</th>
                                            <th className="px-4 py-4 text-[10px] font-bold text-indigo-600 uppercase tracking-widest text-right bg-indigo-50 w-44">Balance Owed</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm divide-y divide-slate-100">
                                        {reportData.map((row, i) => {
                                            // console.log(row)

                                            if (row.is_opening_summary) {
                                                return (
                                                    <tr key="opening-bal" className="bg-slate-50/50 italic border-b-2 border-slate-200">
                                                        <td className="px-4 py-3 text-[10px] font-bold text-slate-400">PREVIOUS</td>
                                                        <td className="px-4 py-3 font-bold text-slate-500">{row.description}</td>
                                                        <td className="px-4 py-3 text-right">—</td>
                                                        <td className="px-4 py-3 text-right">—</td>
                                                        <td className="px-4 py-3 text-right">—</td>
                                                        <td className="px-4 py-3 text-right font-bold text-indigo-900 bg-indigo-100/50">
                                                            {formatCurrency(row.cumulative_balance, row.currency_code)}
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return (
                                                <tr key={i} className={`hover:bg-slate-50 ${row.is_first_in_tx ? 'border-t-2 border-slate-200' : ''}`}>
                                                    <td className="px-4 py-3 text-xs text-slate-500">
                                                        {row.is_first_in_tx ? new Date(row.transaction_date).toLocaleDateString('en-GB') : ''}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className={`flex flex-col ${row.posting_type === 'credit' ? 'pl-6' : ''}`}>
                                                            <span className={`font-bold ${row.posting_type === 'debit' ? 'text-slate-900' : 'text-slate-600'}`}>
                                                                {row.account_name}
                                                                <span className="ml-2 text-[10px] font-mono opacity-50">[{row.account_code}]</span>
                                                            </span>
                                                            {row.is_first_in_tx && (
                                                                <span className="text-[11px] text-slate-400 italic">{row.description}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs font-mono text-slate-400 text-right">
                                                        {/* 1. Only proceed if is_first_in_tx is true */}
                                                        {row.is_first_in_tx && (

                                                            /* 2. If it's a purchase, make it an Emerald Button */
                                                            row.transaction_type?.startsWith("PURCHASE_INVENTORY_") ? (
                                                                <button
                                                                    onClick={() => handlePurchaseOrder(row.ref)}
                                                                    className="text-emerald-600 font-bold underline hover:text-emerald-800 transition-colors cursor-pointer font-mono"
                                                                >
                                                                    {row.ref}
                                                                </button>
                                                            ) : (
                                                                /* 3. Otherwise, just show the plain text ref */
                                                                row.ref
                                                            )

                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-left text-[11px] text-slate-400 italic">
                                                        {row.creator_name}
                                                    </td>

                                                    {/* DEBIT COLUMN: Payments decrease balance */}
                                                    {/* <td className="px-4 py-3 text-right font-mono text-sm text-rose-600">
                                                        {row.posting_type === 'debit' ? formatCurrency(row.amount, row.currency_code) : '—'}
                                                    </td> */}
                                                    <td className={`px-4 py-3 text-right font-mono text-sm relative ${row.posting_type === 'debit' ? 'text-rose-600 font-bold' : 'text-slate-300'}`}>
                                                        <div className="flex items-center justify-end gap-2">
                                                            {row.posting_type === 'debit' && formatCurrency(row.amount, row.currency_code)}

                                                            {/* THE MARKER: Green Arrow Down for Debt Reduction */}
                                                            {row.is_liability_side && row.posting_type === 'debit' && (
                                                                <div className="flex items-center bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-bold border border-emerald-200 shadow-sm animate-pulse">
                                                                    <ArrowDown size={12} className="mr-0.5" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* CREDIT COLUMN: Invoices increase balance */}
                                                    {/* <td className="px-4 py-3 text-right font-mono text-sm text-emerald-600">
                                                        {row.posting_type === 'credit' ? formatCurrency(row.amount, row.currency_code) : '—'}
                                                    </td> */}
                                                    <td className={`px-4 py-3 text-right font-mono text-sm relative ${row.posting_type === 'credit' ? 'text-emerald-600 font-bold' : 'text-slate-300'}`}>
                                                        <div className="flex items-center justify-end gap-2">
                                                            {row.posting_type === 'credit' && formatCurrency(row.amount, row.currency_code)}

                                                            {/* THE MARKER: Red Arrow Up for Debt Increase */}
                                                            {row.is_liability_side && row.posting_type === 'credit' && (
                                                                <div className="flex items-center bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded text-[10px] font-bold border border-rose-200 shadow-sm animate-pulse">
                                                                    <ArrowUp size={12} className="mr-0.5" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* RUNNING BALANCE: Calculated strictly on the AP side */}
                                                    <td className="px-4 py-3 text-right font-bold text-indigo-700 bg-indigo-50/30">
                                                        {formatCurrency(row.cumulative_balance, row.currency_code)}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>


            </div>
            <Modal
                isOpen={isGoodsReceiptModalOpen}
                onClose={() => { setIsGoodsReceiptModalOpen(false); setCurrentGoodsReceipt(null); }}
                title="Goods Receipt Document"
                size="wide"
            >
                <GoodsReceiptDocument data={currentGoodsReceipt} isLoading={isFetchingGoodsReceipt} formatDate={formatDate} formatCurrency={formatCurrency} />
            </Modal>
        </div>
    );
};

export default VendorSubledgerReport;
